import { UserStatus } from "shared-core/types/user.ts";
import type { RiskAnalysis } from "./risk-analysis";

export enum SignupSource {
  NONE = "none",
  TRYGG_HANSA = "trygg_hansa",
  GJENSIDIGE = "gjensidige",
  IF = "if",
  SVEDEA = "svedea",
}

export enum OrganizationType {
  BUSINESS = "business",
  PERSONAL = "personal",
}

export const SIGNUP_SOURCE_COOKIE: string = "controla_signup_source";
export const ORGANIZATION_TYPE_COOKIE: string = "controla_organization_type";

/** Source platform for OAuth flows. Only AGENT is persisted in state — undefined means web. */
export enum OAuthSource {
  AGENT = "agent",
}

export enum OrganizationStatus {
  PENDING = "PENDING",
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  EXPIRED = "EXPIRED",
  CANCELLED = "CANCELLED",
  DELETED = "DELETED",
}

/**
 * The part of the organization every signed-in user may read, members
 * included. Anything added here is visible to non-admins.
 */
export interface OrganizationResponse {
  id: number;
  domain: string;
  /**
   * Mail and productivity provider the organization runs on, resolved from a
   * connected integration, the latest risk analysis, or an MX lookup. Null when
   * the domain runs on something we do not recognize.
   */
  productivity_suite: IdentityProvider | null;
}

/** The full organization payload. Admin only. */
export interface AdminOrganizationResponse extends OrganizationResponse {
  /** Changes only when a complete replacement report is published. */
  risk_analysis_id: number | null;
  risk_analysis: RiskAnalysis | null;
  identity_providers: IdentityProvider[];
  metrics: {
    stopped_attacks: {
      failed_logins: number | null;
      blocked_spam: number | null;
    };
  };
}

/**
 * The organization as the clients hold it: the member-safe fields are always
 * there, the admin-only ones only when the signed-in user is an admin.
 */
export type ClientOrganization = OrganizationResponse &
  Partial<AdminOrganizationResponse>;

export interface OrganizationOverview {
  totalUsersCount: number;
  onboardingOverview: {
    onboardedCount: number;
    users: Array<{ id: number; status: UserStatus }>;
  };
  protectionSummary: {
    fullyProtectedCount: number;
    users: Array<{ id: number; openTasks: number }>;
  };
  highRiskSummary: {
    protectedCount: number;
    users: Array<{ id: number; openTasks: number }>;
  };
  mfaOverview: {
    enabledCount: number;
    users: Array<{ id: number; isMfaEnabled: boolean }>;
  };
  agentInstallationSummary: {
    agentInstalledCount: number;
    users: Array<{ id: number; isInstalled: boolean }>;
  };
  activitySummary: {
    inactiveUsersCount: number;
    users: Array<{
      id: number;
      lastTaskCompletedAt: Date | null;
      openTasks: number;
    }>;
  };
}

export interface Identity {
  provider: IdentityProvider;
  identifier: string;
}

export enum IdentityProvider {
  GOOGLE = "google",
  FACEBOOK = "facebook",
  MICROSOFT = "microsoft",
  GITHUB = "github",
  LINKEDIN = "linkedin",
  X_SOCIAL = "x_social",
  APPLE = "apple",
  EMAIL = "email",
}

export const LoginProviders: IdentityProvider[] = [
  IdentityProvider.GOOGLE,
  IdentityProvider.MICROSOFT,
  IdentityProvider.EMAIL,
];
